import { FC, MouseEvent, useRef, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { Input, Label } from '@/components/Forms';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { paymentType } from '@/utils/enums';
import { banks, countries } from '@/utils/data/payments';

const InputWrap = styled.div`
  position: relative;
`;

export const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const Autocomplete = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const AutocompleteButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

const AttachmentBox = styled.div<{
  $isGray?: boolean;
}>`
  grid-column: span 2;
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ $isGray }) =>
    $isGray &&
    css`
      margin: 32px 0;
    `};
`;


const swiftCodes = [
  { bank: 'Swedbank', code: 'HABALT22' },
  { bank: 'SEB', code: 'CBVILT2X' },
  { bank: 'Luminor', code: 'AGBLLT2X' },
  { bank: 'Šiaulių bankas', code: 'CBSBLT26' },
  { bank: 'Citadele', code: 'INDULT2X' },
  { bank: 'Medicinos bankas', code: 'MDBALT22' },
  { bank: 'Paysera', code: 'EVIULT2VXXX' },
];

const getPaymentTypeFromCountry = (countryCode: string) => {
  if (countryCode === 'LT') return paymentType.DOMESTIC;
  return paymentType.EUROPEAN;
};

const getCountryFromBic = (bic: string) => {
  const countryCode = bic.slice(4, 6).toUpperCase();
  return countries.find((c) => c.value === countryCode) ?? null;
};

const getBankFromIban = (rawIban: string) => {
  const iban = (rawIban || '').replace(/\s/g, '').toUpperCase();
  if (iban.length < 9) return null;

  const countryCode = iban.slice(0, 2);
  const bankCode = iban.slice(4, 9);

  const country = countries.find((c) => c.value === countryCode) ?? null;
  const bank = banks.find((b) => b.code.includes(bankCode)) ?? null;

  return { country, bank };
};

const highlightMatch = (str: string, query: string) => {
  if (!query) return str;
  const regExp = new RegExp(query, 'gi');
  return str.replace(regExp, (match) => `<span>${match}</span>`);
};

interface Props {
  control: Control<any>;
  name: string;
  bankFieldName: string;
  ibanFieldName: string;
  countryFieldName: string;
  paymentTypeFieldName: string;
}

const InvoicesSwiftCodesField: FC<Props> = ({
  control,
  name,
  bankFieldName,
  ibanFieldName,
  countryFieldName,
  paymentTypeFieldName,
}) => {
  const [isBankFocused, setIsBankFocused] = useState(false);
  const [isSwiftFocused, setIsSwiftFocused] = useState(false);
  const [bankSearchQuery, setBankSearchQuery] = useState('');

  const fieldRef = useRef<HTMLDivElement>(null);

  const { field: ibanField, fieldState: ibanFieldState } = useController({ control, name: ibanFieldName });
  const { field: bankField, fieldState: bankFieldState } = useController({ control, name: bankFieldName });
  const { field: swiftField, fieldState: swiftFieldState } = useController({ control, name });
  const { field: countryField } = useController({ control, name: countryFieldName });
  const { field: paymentTypeField } = useController({ control, name: paymentTypeFieldName });

  useOnClickOutside(fieldRef, () => {
    setIsBankFocused(false);
    setIsSwiftFocused(false);
    setBankSearchQuery('');
  });

  const handleIbanChange = (rawIban: string) => {
    ibanField.onChange(rawIban);

    const result = getBankFromIban(rawIban);
    if (!result) return;

    if (result.country) {
      countryField.onChange(result.country.value);
      paymentTypeField.onChange(getPaymentTypeFromCountry(result.country.value));
    }

    if (result.bank) {
      bankField.onChange(result.bank.bank);
      swiftField.onChange(result.bank.bic);
    }
  };

  const handleBankSelect = (bank: (typeof banks)[0]) => {
    bankField.onChange(bank.bank);
    swiftField.onChange(bank.bic);

    const country = getCountryFromBic(bank.bic);
    if (country) {
      countryField.onChange(country.value);
      paymentTypeField.onChange(getPaymentTypeFromCountry(country.value));
    }

    setBankSearchQuery('');
    setIsBankFocused(false);
  };

  const bankSuggestions = banks.filter((b) => {
    if (!bankSearchQuery) return true;
    return (
      b.bank.toLowerCase().includes(bankSearchQuery.toLowerCase()) ||
      b.bic.toLowerCase().includes(bankSearchQuery.toLowerCase())
    );
  });

  const filteredSwiftCodes = swiftCodes.filter((s) => {
    if (!swiftField.value) return true;
    return (
      s.code.toLowerCase().includes(swiftField.value.toLowerCase()) ||
      s.bank.toLowerCase().includes(swiftField.value.toLowerCase())
    );
  });

  return (
    <AttachmentBox ref={fieldRef}>
      <InputWrap>
        <Label htmlFor={ibanFieldName}>Account number</Label>
        <Input
          onChange={(e) => handleIbanChange(e.target.value)}
          placeholder="Add account number"
          value={ibanField.value || ''}
          $hasError={!!ibanFieldState.error}
          id={ibanFieldName}
        />
        {!!ibanFieldState.error && (
          <ValidationMessage>{ibanFieldState.error?.message}</ValidationMessage>
        )}
      </InputWrap>
      <InputWrap>
        <Label htmlFor={bankFieldName}>Creditor bank</Label>
        <Input
          onChange={(e) => {
            setBankSearchQuery(e.target.value);
            bankField.onChange(e.target.value);
          }}
          placeholder="Add bank"
          value={isBankFocused ? bankSearchQuery : (bankField.value || '')}
          $hasError={!!bankFieldState.error}
          id={bankFieldName}
          onFocus={() => {
            setBankSearchQuery('');
            setIsBankFocused(true);
          }}
        />
        {!!bankFieldState.error && (
          <ValidationMessage>{bankFieldState.error?.message}</ValidationMessage>
        )}
        {isBankFocused && bankSuggestions.length > 0 && (
          <Autocomplete>
            {bankSuggestions.map((bank, index) => (
              <AutocompleteButton
                key={index}
                type="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleBankSelect(bank);
                }}
              >
                <Text
                  $color="gray"
                  $type="captionSemibold"
                  dangerouslySetInnerHTML={{
                    __html: highlightMatch(bank.bank, bankSearchQuery),
                  }}
                />
              </AutocompleteButton>
            ))}
          </Autocomplete>
        )}
      </InputWrap>
      <InputWrap>
        <Label htmlFor={name}>Swift code</Label>
        <Input
          onChange={(e) => swiftField.onChange(e.target.value)}
          placeholder="Add swift code"
          value={swiftField.value || ''}
          $hasError={!!swiftFieldState.error}
          id={name}
          onFocus={() => setIsSwiftFocused(true)}
        />
        {!!swiftFieldState.error && (
          <ValidationMessage>{swiftFieldState.error?.message}</ValidationMessage>
        )}
        {isSwiftFocused && filteredSwiftCodes.length > 0 && (
          <Autocomplete>
            {filteredSwiftCodes.map((suggestion, index) => (
              <AutocompleteButton
                key={index}
                type="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  swiftField.onChange(suggestion.code);
                  setIsSwiftFocused(false);
                }}
              >
                <Text
                  $color="gray"
                  $type="captionSemibold"
                  dangerouslySetInnerHTML={{
                    __html: `${highlightMatch(suggestion.code, swiftField.value)} · ${highlightMatch(suggestion.bank, swiftField.value)}`,
                  }}
                />
              </AutocompleteButton>
            ))}
          </Autocomplete>
        )}
      </InputWrap>
    </AttachmentBox>
  );
};

export default InvoicesSwiftCodesField;
